import { Input, Select } from '@/components/ui/FormControls';
import { parseCalendarDate } from '@klothiq/domain';
// ─────────────────────────────────────────────
// KLOTHIQ — Plan Detail Page
// ─────────────────────────────────────────────

import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Target,
  Clock,
  AlertTriangle,
  Archive,
  Edit2,
  Search,
  ListChecks,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePlanDetails, useArchivePlan } from '@/hooks/usePlans';
import PlanFormModal from './PlanFormModal';
import TaskFormModal from './TaskFormModal';
import TaskDetailModal from '../tasks/TaskDetailModal';
import ErrorState from '@/components/common/ErrorState';
import { useWorkIntent } from '@/hooks/useWorkIntent';

export default function PlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  const canManage = can('plans.manage');

  const { data, isLoading, isError, error, refetch } = usePlanDetails(planId);
  const archivePlan = useArchivePlan();
  const archiveIntent = useWorkIntent();

  // Modals state
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const selectedTask = data?.tasks.find((task) => task.id === selectedTaskId) ?? null;

  // Filters state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Check URL query param ?taskId=... to open modal
  useEffect(() => {
    const queryTaskId = searchParams.get('taskId');
    if (queryTaskId) setSelectedTaskId(queryTaskId);
  }, [searchParams]);

  const plan = data?.plan;
  const metrics = data?.metrics;
  const tasks = data?.tasks || [];

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (search) {
        const s = search.toLowerCase();
        const matchesTitle = t.title.toLowerCase().includes(s);
        const matchesDesc = t.description?.toLowerCase().includes(s);
        const matchesAssignee = t.assignee_name?.toLowerCase().includes(s);
        if (!matchesTitle && !matchesDesc && !matchesAssignee) return false;
      }
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      if (assigneeFilter !== 'all') {
        if (assigneeFilter === 'unassigned' && t.assignee_id) return false;
        if (assigneeFilter !== 'unassigned' && t.assignee_id !== assigneeFilter) return false;
      }
      if (overdueOnly && !t.is_overdue) return false;
      return true;
    });
  }, [tasks, search, statusFilter, priorityFilter, assigneeFilter, overdueOnly]);

  // List of unique assignees in this plan for filter dropdown
  const uniqueAssignees = useMemo(() => {
    const map = new Map<string, string>();
    tasks.forEach((t) => {
      if (t.assignee_id && t.assignee_name) {
        map.set(t.assignee_id, t.assignee_name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [tasks]);

  const handleArchiveToggle = async () => {
    if (!plan || archivePlan.isPending) return;
    if (
      window.confirm(
        plan.is_archived
          ? `Unarchive plan "${plan.title}"?`
          : `Archive plan "${plan.title}"? Active tasks will be preserved.`,
      )
    ) {
      const input = {
        plan_id: plan.id,
        expected_version: plan.version,
        archived: !plan.is_archived,
      };
      try {
        await archivePlan.mutateAsync({
          ...input,
          request_key: archiveIntent.key('archive_plan', input),
        });
        archiveIntent.complete('archive_plan');
      } catch {
        /* The mutation displays the failure and preserves the retry intent. */
      }
    }
  };

  const handleCloseTaskModal = () => {
    setSelectedTaskId(null);
    if (searchParams.has('taskId')) {
      searchParams.delete('taskId');
      setSearchParams(searchParams);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">Loading plan details...</p>
      </div>
    );
  }

  if (isError && !data)
    return (
      <div className="p-4 sm:p-6">
        <ErrorState
          title="Plan details could not be loaded"
          message={error instanceof Error ? error.message : 'Try again.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  if (!plan) {
    return (
      <div className="flex-1 p-8 text-center">
        <p className="text-base font-semibold text-foreground">Plan not found</p>
        <button
          onClick={() => navigate('/plans')}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Plans
        </button>
      </div>
    );
  }

  const priorityStyles =
    {
      urgent: 'bg-rose-500/15 text-rose-500 border-rose-500/30',
      high: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
      normal: 'bg-primary/15 text-primary border-primary/30',
      low: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
    }[plan.priority] || 'bg-muted text-muted-foreground border-border';

  return (
    <div className="flex-1 space-y-6 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto overflow-y-auto">
      {isError && (
        <div role="alert" className="rounded-xl border border-destructive/30 p-3 text-sm">
          The latest details could not be refreshed. Your open draft has been kept.{' '}
          <button className="min-h-11 underline" onClick={() => refetch()}>
            Retry refresh
          </button>
        </div>
      )}
      {/* Back Button & Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate('/plans')}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Plans</span>
        </button>

        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setIsEditPlanOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors"
            >
              <Edit2 className="h-3.5 w-3.5" />
              <span>Edit Plan</span>
            </button>

            <button
              onClick={handleArchiveToggle}
              disabled={archivePlan.isPending}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors"
            >
              <Archive className="h-3.5 w-3.5" />
              <span>{plan.is_archived ? 'Unarchive' : 'Archive'}</span>
            </button>

            {!plan.is_archived && (
              <button
                onClick={() => setIsCreateTaskOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all"
              >
                <Plus className="h-4 w-4" />
                <span>Add Task</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Plan Header Card */}
      <div className="rounded-2xl border border-border bg-card/70 p-6 backdrop-blur-sm shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="space-y-2 max-w-3xl">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${priorityStyles}`}
              >
                {plan.priority} Priority
              </span>
              {plan.is_archived && (
                <span className="rounded-full bg-zinc-500/20 text-zinc-400 border border-zinc-500/30 px-2 py-0.5 text-[10px] font-semibold">
                  Archived
                </span>
              )}
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-foreground">{plan.title}</h1>

            {plan.goal && (
              <div className="flex items-start gap-2 rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground">
                <Target className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold text-primary">Strategic Goal: </span>
                  <span>{plan.goal}</span>
                </div>
              </div>
            )}

            {plan.description && (
              <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {plan.description}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2 shrink-0 md:w-64 text-xs">
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Start Date:</span>
              <span className="font-medium text-foreground">
                {plan.start_date
                  ? parseCalendarDate(plan.start_date)?.toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Target Date:</span>
              <span className="font-medium text-foreground">
                {plan.target_date
                  ? parseCalendarDate(plan.target_date)?.toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
            {plan.created_by_name && (
              <div className="flex items-center justify-between text-muted-foreground pt-1 border-t border-border">
                <span>Created by:</span>
                <span className="font-medium text-foreground">{plan.created_by_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Aggregate Progress Bar */}
        <div className="pt-2 border-t border-border/60">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-foreground">Overall Progress</span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                {metrics?.progress_percent || 0}%
              </span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span>{metrics?.completed_tasks || 0} completed</span>
              <span>•</span>
              <span>{metrics?.in_progress_tasks || 0} in progress</span>
              <span>•</span>
              <span>{metrics?.blocked_tasks || 0} blocked</span>
              <span>•</span>
              <span>{metrics?.todo_tasks || 0} todo</span>
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${metrics?.progress_percent || 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Task Filters and List */}
      <div className="space-y-4">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tasks or assignees..."
                className="w-full rounded-xl border border-input bg-card pl-9 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                helpLabel="Search records"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Status Filter */}
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-input bg-card px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Filter status"
            >
              <option value="all">All Statuses</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>

            {/* Priority Filter */}
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="rounded-xl border border-input bg-card px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Filter priority"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </Select>

            {/* Assignee Filter */}
            <Select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="rounded-xl border border-input bg-card px-3 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Filter assignee"
            >
              <option value="all">All Assignees</option>
              <option value="unassigned">Unassigned</option>
              {uniqueAssignees.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>

            {/* Overdue filter */}
            <button
              type="button"
              onClick={() => setOverdueOnly((prev) => !prev)}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold border transition-all ${
                overdueOnly
                  ? 'bg-rose-500/15 border-rose-500/40 text-rose-500'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              Overdue Only
            </button>
          </div>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
            <ListChecks className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-foreground">No tasks match criteria</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {tasks.length === 0
                ? 'No tasks created yet for this plan.'
                : 'Try adjusting your search or filters.'}
            </p>
            {canManage && !plan.is_archived && (
              <button
                onClick={() => setIsCreateTaskOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add First Task</span>
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map((t) => {
              const taskPriorityStyle =
                {
                  urgent: 'text-rose-500 bg-rose-500/10 border-rose-500/20',
                  high: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
                  normal: 'text-primary bg-primary/10 border-primary/20',
                  low: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
                }[t.priority] || 'text-muted-foreground bg-muted border-border';

              const taskStatusStyle =
                {
                  completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
                  in_progress: 'bg-primary/10 text-primary border-primary/20',
                  blocked: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
                  todo: 'bg-muted text-muted-foreground border-border',
                  cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
                }[t.status] || 'bg-muted text-muted-foreground border-border';

              return (
                <div
                  key={t.id}
                  onClick={() => setSelectedTaskId(t.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (
                      event.target === event.currentTarget &&
                      (event.key === 'Enter' || event.key === ' ')
                    ) {
                      event.preventDefault();
                      setSelectedTaskId(t.id);
                    }
                  }}
                  className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-border bg-card/60 p-4 hover:border-primary/50 hover:bg-card transition-all cursor-pointer shadow-sm"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${taskPriorityStyle}`}
                      >
                        {t.priority}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${taskStatusStyle}`}
                      >
                        {t.status.replace('_', ' ')}
                      </span>
                      {t.is_overdue && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-500/30 px-2 py-0.5 text-[9px] font-semibold text-rose-500">
                          <Clock className="h-2.5 w-2.5" /> Overdue
                        </span>
                      )}
                    </div>

                    <h4 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                      {t.title}
                    </h4>

                    {t.blocker_note && (
                      <div className="flex items-center gap-1 text-[11px] text-amber-500 font-medium">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        <span className="line-clamp-1">Blocker: {t.blocker_note}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs pt-2 sm:pt-0 border-t sm:border-t-0 border-border/50">
                    {/* Assignee */}
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:min-w-[100px]">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">
                        {t.assignee_name ? t.assignee_name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <span className="text-xs text-muted-foreground truncate">
                        {t.assignee_name || 'Unassigned'}
                      </span>
                    </div>

                    {/* Due Date */}
                    <div className="min-w-[90px] text-right">
                      {t.due_date ? (
                        <span
                          className={`text-xs ${
                            t.is_overdue ? 'text-rose-500 font-semibold' : 'text-muted-foreground'
                          }`}
                        >
                          {parseCalendarDate(t.due_date)?.toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/60 text-xs">—</span>
                      )}
                    </div>

                    {/* Progress Bar & Value */}
                    <div className="flex items-center gap-2 min-w-[80px]">
                      <div className="h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${t.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-foreground w-7 text-right">
                        {t.progress}%
                      </span>
                    </div>

                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Plan Modal */}
      <PlanFormModal
        isOpen={isEditPlanOpen}
        onClose={() => setIsEditPlanOpen(false)}
        planToEdit={plan}
      />

      {/* Create Task Modal */}
      <TaskFormModal
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        planId={plan.id}
        planTargetDate={plan.target_date}
        planArchived={plan.is_archived}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={!!selectedTask}
        onClose={handleCloseTaskModal}
        task={selectedTask}
        planId={plan.id}
        onReload={async () => {
          const result = await refetch();
          if (result.error) throw result.error;
          return result.data?.tasks.find((task) => task.id === selectedTaskId);
        }}
      />
      {selectedTaskId && !selectedTask && (
        <div role="status" className="rounded-xl border border-border p-4 text-sm">
          This task is no longer available in this plan.{' '}
          <button type="button" className="min-h-11 underline" onClick={handleCloseTaskModal}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
